import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider } from "firebase/auth";
import { getFirestore } from "firebase/firestore";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const requiredFirebaseFields = [
  "apiKey",
  "authDomain",
  "projectId",
  "storageBucket",
  "messagingSenderId",
  "appId",
];

const hasFirebaseConfig = requiredFirebaseFields.every((field) => {
  const value = firebaseConfig[field];
  return typeof value === "string" && value.trim().length > 0;
});

export const isFirebaseConfigured = hasFirebaseConfig;

const noopAuth = {
  currentUser: null,
  onAuthStateChanged: () => () => undefined,
};

if (!hasFirebaseConfig) {
  console.warn(
    "Firebase is not configured for this frontend. Set the REACT_APP_FIREBASE_* environment values to enable auth and Firestore."
  );
}

const app = hasFirebaseConfig ? initializeApp(firebaseConfig) : null;
export const firestore = hasFirebaseConfig ? getFirestore(app) : null;
// Firebase Storage is not used by any page; its SDK (11 KB of the main
// bundle) was loaded for an export nothing imported (removed 2026-10-31).
export const auth = hasFirebaseConfig ? getAuth(app) : noopAuth;
/**
 * The Google sign-in provider.
 *
 * `prompt: 'select_account'` is not a nicety — it is the fix for a real
 * failure (2026-09-10, reported on localhost AND production, and "was working
 * fine but stopped somehow").
 *
 * Built bare, the provider sends no `prompt`, so Google resolves which account
 * to use from the browser's own session INDEX. With more than one Google
 * account signed in, it silently picks one — the founder's error page was
 * `accounts.google.com/signin/oauth/v3/consent?authuser=1`, the SECOND
 * account — and when that index does not line up with a valid session for this
 * client, the consent endpoint answers with a bare "401 … the request is
 * malformed". Nothing is wrong with the app; the request never named an
 * account, so Google guessed and guessed wrong. It starts the day a person
 * adds a second Google account to their browser, which is why it began without
 * a deploy.
 *
 * Forcing the chooser means every authorization request carries an account the
 * person just picked. It also removes the "signed in as the wrong Google
 * account" class of support ticket entirely.
 */
export const googleProvider = (() => {
  if (!hasFirebaseConfig) return null;
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  return provider;
})();